import { type RefObject, useCallback, useMemo } from 'react';
import type { Measurable } from '@radix-ui/rect';

import type {
  ImageBlockAspect,
  ImageBlockFit,
  ImageBlockShape,
} from '@/components/blocks/primitives/image-block';
import {
  IMAGE_BLOCK_ASPECT_VALUES,
  IMAGE_BLOCK_FIT_VALUES,
  IMAGE_BLOCK_SHAPE_VALUES,
} from '@/components/blocks/primitives/image-block';
import { selectedImageAspect } from '@/components/blocks/primitives/image-presentation';
import {
  type ImageCropResult,
  ImageSettingsPopover,
} from '@/components/shared/image-settings-modal';
import { useBlockSettingsState } from '@/hooks/use-block-settings-state';
import { type RequestedImageSize, readImageSize } from '@/lib/blocks/image-size';
import { type GroupChildFloat, getBlockFloat } from '@/lib/blocks/layout/group-layout';
import { scheduleScrollToPageEditorBlock } from '@/lib/page-editor/scroll-to-block';
import {
  DEFAULT_FOCUS_POINT,
  pickStringUnion,
  readAssetId,
  readBoolean,
  readFolderId,
  readImageUrl,
  readImageValue,
  readNumber,
  readString,
} from '@/lib/prop-utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import { getBlockUiKey, type PageBlock } from '@/types/block';
import type { ImageSuggestionTarget, ImageValue } from '@/types/image';

interface ImageBlockSettingsModalProps {
  sectionId: string;
  block: PageBlock;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorRef?: RefObject<Measurable | null>;
  showFloatOption?: boolean;
}

export function ImageBlockSettingsModal({
  sectionId,
  block,
  open,
  onOpenChange,
  anchorRef,
  showFloatOption = false,
}: ImageBlockSettingsModalProps) {
  const updateBlock = usePageEditorStore((state) => state.updateBlock);
  const previewBlockPropsWithFloatLayout = usePageEditorStore(
    (state) => state.previewBlockPropsWithFloatLayout,
  );
  const cancelBlockPropsWithFloatLayoutPreview = usePageEditorStore(
    (state) => state.cancelBlockPropsWithFloatLayoutPreview,
  );

  const applyChange = useCallback(
    (value: Record<string, unknown> | null, commit: boolean) => {
      const props = value ?? {};
      if (commit) {
        updateBlock(sectionId, block.id, { props });
        return;
      }
      previewBlockPropsWithFloatLayout(sectionId, block.id, props);
    },
    [sectionId, block.id, previewBlockPropsWithFloatLayout, updateBlock],
  );
  const imageSuggestionTarget = useMemo<ImageSuggestionTarget>(
    () => ({ kind: 'block', blockId: block.id }),
    [block.id],
  );
  const cancelPreview = useCallback(
    () => cancelBlockPropsWithFloatLayoutPreview(sectionId, block.id),
    [block.id, cancelBlockPropsWithFloatLayoutPreview, sectionId],
  );

  const { persistMedia, applyPreview, handleSave, handleCancel } = useBlockSettingsState({
    open,
    currentValue: block.props || {},
    mediaKey: 'image',
    applyChange,
    cancelPreview,
  });

  const blockProps = block.props || {};
  const currentImage = readImageValue(blockProps.image);
  const currentAssetId = readAssetId(blockProps.image) || null;
  const currentFolderId = readFolderId(blockProps.image) || null;
  const currentImageUrl = readImageUrl(blockProps.image) || null;
  const currentFit = pickStringUnion(
    readString(blockProps.fit),
    IMAGE_BLOCK_FIT_VALUES,
    'cover',
  ) as ImageBlockFit;
  const currentShape = pickStringUnion(
    readString(blockProps.shape),
    IMAGE_BLOCK_SHAPE_VALUES,
    'rounded',
  ) as ImageBlockShape;
  const currentAspect = pickStringUnion(
    readString(blockProps.aspect),
    IMAGE_BLOCK_ASPECT_VALUES,
    'wide',
  );
  const currentFocusX = readNumber(blockProps.focusX) ?? DEFAULT_FOCUS_POINT;
  const currentFocusY = readNumber(blockProps.focusY) ?? DEFAULT_FOCUS_POINT;
  const currentFloat = getBlockFloat(block);
  const blockUiKey = getBlockUiKey(block);
  const canResizeImage = readBoolean(blockProps.fullBleed) !== true;

  const handleImageChange = useCallback(
    (image: ImageValue | null) => {
      // Send explicit image + assetId + url triples so autosave's slot_content
      // patch overwrites any stale asset_id previously written by publish
      // (see Pages::SpecialPageLayouts::TemplateRenderer#build).
      if (image === null) {
        applyPreview({
          image: null,
          assetId: null,
          url: null,
          width: null,
          height: null,
          imageSize: null,
        });
      } else {
        persistMedia({
          image,
          assetId: image.assetId ?? null,
          url: image.url ?? null,
          width: null,
          height: null,
          imageSize: null,
          aspect: selectedImageAspect(currentAspect),
        });
      }
    },
    [applyPreview, persistMedia, currentAspect],
  );

  const handleAltTextChange = useCallback(
    (altText: string) => {
      const img = readImageValue(block.props?.image);
      if (!img) return;
      applyPreview({ image: { ...img, alt: altText } });
    },
    [applyPreview, block.props?.image],
  );

  const handleImageCropped = useCallback(
    ({ image, width, height }: ImageCropResult) => {
      persistMedia({
        image,
        assetId: image.assetId ?? null,
        url: image.url ?? null,
        width: width ?? null,
        height: height ?? null,
      });
    },
    [persistMedia],
  );

  const handleFitChange = useCallback(
    (fit: ImageBlockFit) => applyPreview({ fit }),
    [applyPreview],
  );

  const handleShapeChange = useCallback(
    (shape: ImageBlockShape) => applyPreview({ shape }),
    [applyPreview],
  );

  const handleAspectChange = useCallback(
    (aspect: ImageBlockAspect) => applyPreview({ aspect }),
    [applyPreview],
  );

  const handleFocusChange = useCallback(
    (focusX: number, focusY: number) => applyPreview({ focusX, focusY }),
    [applyPreview],
  );

  const handleImageSizeChange = useCallback(
    (imageSize: RequestedImageSize) =>
      applyPreview({ imageSize: imageSize === 'auto' ? null : imageSize }),
    [applyPreview],
  );

  const handleFloatChange = useCallback(
    (float: GroupChildFloat | null) => {
      if (float === currentFloat) return;
      applyPreview({ float });
      scheduleScrollToPageEditorBlock(blockUiKey);
    },
    [applyPreview, blockUiKey, currentFloat],
  );

  return (
    <ImageSettingsPopover
      duplicateOnCrop
      open={open}
      onOpenChange={onOpenChange}
      image={currentImage}
      fit={currentFit}
      shape={currentShape}
      showSettings
      selectedAssetId={currentAssetId}
      selectedFolderId={currentFolderId}
      initialImageUrl={currentAssetId ? undefined : currentImageUrl}
      sectionId={sectionId}
      imageSuggestionTarget={imageSuggestionTarget}
      enableCreateImage={true}
      anchorRef={anchorRef}
      onImageChange={handleImageChange}
      onImageCropped={handleImageCropped}
      onAltTextChange={handleAltTextChange}
      onFitChange={handleFitChange}
      onShapeChange={handleShapeChange}
      aspect={currentAspect}
      onAspectChange={handleAspectChange}
      imageSize={readImageSize(blockProps.imageSize)}
      onImageSizeChange={canResizeImage ? handleImageSizeChange : undefined}
      focusX={currentFocusX}
      focusY={currentFocusY}
      onFocusChange={handleFocusChange}
      float={currentFloat}
      onFloatChange={showFloatOption ? handleFloatChange : undefined}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  );
}
